import React, { useMemo, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierStore, Driver, Job } from '../../lib/supplierStore';
import { toast } from 'sonner';
import { Plus, Star, Phone, X, Truck as TruckIcon, User, IdCard, Calendar, Search, List, CalendarDays, MapPin, Clock, CheckCircle2, AlertCircle, History, Sparkles, Upload, Download, FileSpreadsheet } from 'lucide-react';
interface AddDriverModalProps {
  open: boolean;
  onClose: () => void;
}
function AddDriverModal({
  open,
  onClose
}: AddDriverModalProps) {
  const {
    trucks,
    drivers,
    addDriver
  } = useSupplierStore();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+971 ');
  const [license, setLicense] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [truckId, setTruckId] = useState('');
  const availableTrucks = useMemo(() => {
    return trucks.filter(t => !t.assignedDriverId || !drivers.find(d => d.id === t.assignedDriverId));
  }, [trucks, drivers]);
  const reset = () => {
    setName('');
    setPhone('+971 ');
    setLicense('');
    setLicenseExpiry('');
    setTruckId('');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !license.trim() || !licenseExpiry) {
      toast.error('Please fill in all required fields.');
      return;
    }
    addDriver({
      name: name.trim(),
      phone: phone.trim(),
      license: license.trim(),
      licenseExpiry,
      truckId: truckId || undefined
    });
    toast.success(`${name.trim()} added to driver roster.`);
    reset();
    onClose();
  };
  return <AnimatePresence data-id="element-2869">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-2870" />
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 350,
        damping: 30
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2871">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col" data-id="element-2872">
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-2873">
                <div data-id="element-2874">
                  <h2 className="text-xl font-display font-bold text-gray-900" data-id="element-2875">
                    Add Driver
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-2876">
                    Register a new driver to your roster
                  </p>
                </div>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-2877">
                  <X className="w-5 h-5 text-gray-500" data-id="element-2878" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-id="element-2879">
                <Field label="Full Name" icon={<User className="w-4 h-4 text-gray-400" data-id="element-2881" />} required data-id="element-2880">
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Ahmed Khan" className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2882" />
                </Field>

                <Field label="Phone Number" icon={<Phone className="w-4 h-4 text-gray-400" data-id="element-2884" />} required data-id="element-2883">
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 50 123 4567" className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2885" />
                </Field>

                <Field label="License Number" icon={<IdCard className="w-4 h-4 text-gray-400" data-id="element-2887" />} required data-id="element-2886">
                  <input type="text" value={license} onChange={e => setLicense(e.target.value)} placeholder="DL-123456" className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2888" />
                </Field>

                <Field label="License Expiry" icon={<Calendar className="w-4 h-4 text-gray-400" data-id="element-2890" />} required data-id="element-2889">
                  <input type="date" value={licenseExpiry} onChange={e => setLicenseExpiry(e.target.value)} className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2891" />
                </Field>

                <div data-id="element-2892">
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2" data-id="element-2893">
                    Assign Truck (optional)
                  </label>
                  <div className="relative" data-id="element-2894">
                    <TruckIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-2895" />
                    <select value={truckId} onChange={e => setTruckId(e.target.value)} className="w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 appearance-none" data-id="element-2896">
                      <option value="" data-id="element-2897">No truck assigned</option>
                      {availableTrucks.map(t => <option key={t.id} value={t.id} data-id="element-2898">
                          {t.plate} — {t.type}
                        </option>)}
                    </select>
                  </div>
                  {availableTrucks.length === 0 && <p className="text-[11px] text-amber-700 mt-1.5" data-id="element-2899">
                      All trucks are currently assigned. You can re-assign from
                      the Fleet page.
                    </p>}
                </div>
              </form>

              <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end shrink-0 bg-gray-50/50" data-id="element-2900">
                <button type="button" onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-bold transition-colors" data-id="element-2901">
                  Cancel
                </button>
                <button type="button" onClick={handleSubmit} className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors flex items-center gap-2" data-id="element-2902">
                  <Plus className="w-4 h-4" data-id="element-2903" /> Add Driver
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function Field({
  label,
  icon,
  required,
  children
}: {
  label: string;
  icon: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div data-id="element-2904">
      <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2" data-id="element-2905">
        {label} {required && <span className="text-brand-500" data-id="element-2906">*</span>}
      </label>
      <div className="relative" data-id="element-2907">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-2908">
          {icon}
        </div>
        {children}
      </div>
    </div>;
}
function formatTime(hour: number) {
  const h = Math.floor(hour);
  const m = (hour - h) * 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m === 0 ? '00' : m} ${ampm}`;
}
function getJobStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-700 border-green-100';
    case 'in_progress':
    case 'at_pickup':
    case 'picked':
    case 'at_dropoff':
      return 'bg-purple-50 text-purple-700 border-purple-100';
    case 'accepted':
      return 'bg-blue-50 text-blue-700 border-blue-100';
    case 'pending_acceptance':
      return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-100';
  }
}
function getJobStatusLabel(status: string): string {
  const map: Record<string, string> = {
    pending_acceptance: 'Pending',
    accepted: 'Accepted',
    in_progress: 'In progress',
    at_pickup: 'At pickup',
    picked: 'Picked',
    at_dropoff: 'At dropoff',
    completed: 'Completed',
    unassigned: 'Unassigned'
  };
  return map[status] || status;
}
function DriverDetailDrawer({
  driver,
  jobs,
  trucks,
  drivers,
  onClose
}: {
  driver: Driver | null;
  jobs: Job[];
  trucks: ReturnType<typeof useSupplierStore>['trucks'];
  drivers: Driver[];
  onClose: () => void;
}) {
  const {
    assignTruckToDriver
  } = useSupplierStore();
  const [truckPickerOpen, setTruckPickerOpen] = useState(false);
  if (!driver) return null;
  const driverJobs = jobs.filter(j => j.assignedDriverId === driver.id);
  const upcoming = driverJobs.filter(j => j.status !== 'completed').sort((a, b) => {
    const order = {
      today: 0,
      tomorrow: 1,
      yesterday: -1
    } as Record<string, number>;
    const oA = order[a.scheduledDate] ?? 2;
    const oB = order[b.scheduledDate] ?? 2;
    if (oA !== oB) return oA - oB;
    return a.startHour - b.startHour;
  });
  const completed = driverJobs.filter(j => j.status === 'completed').sort((a, b) => (b.stageHistory?.completed || 0) - (a.stageHistory?.completed || 0));
  const truck = trucks.find(t => t.id === driver.assignedTruckId);
  const availableTrucks = trucks.filter(t => {
    if (t.id === driver.assignedTruckId) return false;
    const heldBy = drivers.find(d => d.assignedTruckId === t.id);
    return !heldBy;
  });
  const handleAssignTruck = (truckId: string) => {
    assignTruckToDriver(driver.id, truckId);
    const t = trucks.find(x => x.id === truckId);
    toast.success(`${t?.plate || 'Truck'} assigned to ${driver.name}.`);
    setTruckPickerOpen(false);
  };
  const handleUnassignTruck = () => {
    assignTruckToDriver(driver.id, null);
    toast.success(`Truck unassigned from ${driver.name}.`);
  };
  return <AnimatePresence data-id="element-2909">
      {driver && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 z-50" data-id="element-2910" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col" data-id="element-2911">
            <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between shrink-0" data-id="element-2912">
              <div className="flex items-center gap-4" data-id="element-2913">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-xl" data-id="element-2914">
                  {driver.name.charAt(0)}
                </div>
                <div data-id="element-2915">
                  <h2 className="text-xl font-display font-bold text-gray-900" data-id="element-2916">
                    {driver.name}
                  </h2>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5" data-id="element-2917">
                    <Phone className="w-3 h-3" data-id="element-2918" /> {driver.phone}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5" data-id="element-2919">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-700" data-id="element-2920">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-current" data-id="element-2921" />
                      {driver.rating}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${driver.status === 'On Duty' ? 'bg-green-100 text-green-700' : driver.status === 'Off Duty' ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'}`} data-id="element-2922">
                      {driver.status}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors shrink-0" aria-label="Close" data-id="element-2923">
                <X className="w-5 h-5 text-gray-500" data-id="element-2924" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-gray-100 grid grid-cols-2 gap-3 bg-gray-50/50 shrink-0" data-id="element-2925">
              <Stat label="Upcoming" value={upcoming.length} data-id="element-2926" />
              <Stat label="Completed" value={completed.length} accent="green" data-id="element-2927" />
            </div>

            <div className="flex-1 overflow-y-auto" data-id="element-2928">
              {/* Truck assignment panel */}
              <div className="px-6 pt-5 pb-3" data-id="element-2929">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2930">
                  <TruckIcon className="w-3.5 h-3.5" data-id="element-2931" />
                  Assigned truck
                </h3>
                {truck ? <div className="p-4 rounded-2xl border border-gray-200 bg-white" data-id="element-2932">
                    <div className="flex items-center justify-between gap-3" data-id="element-2933">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2934">
                        <div className="w-11 h-11 rounded-xl bg-gray-900 flex items-center justify-center shrink-0" data-id="element-2935">
                          <TruckIcon className="w-5 h-5 text-white" data-id="element-2936" />
                        </div>
                        <div className="min-w-0" data-id="element-2937">
                          <p className="font-bold text-gray-900 truncate" data-id="element-2938">
                            {truck.plate}
                          </p>
                          <p className="text-xs text-gray-500 truncate" data-id="element-2939">
                            {truck.type} · {truck.capacity}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0" data-id="element-2940">
                        <button onClick={() => setTruckPickerOpen(v => !v)} className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition-colors" data-id="element-2941">
                          Change
                        </button>
                        <button onClick={handleUnassignTruck} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700 text-gray-700 text-xs font-bold transition-colors" data-id="element-2942">
                          Unassign
                        </button>
                      </div>
                    </div>
                    {truckPickerOpen && <TruckPickerList trucks={availableTrucks} onPick={handleAssignTruck} onCancel={() => setTruckPickerOpen(false)} data-id="element-2943" />}
                  </div> : <div className="p-4 rounded-2xl border border-amber-200 bg-amber-50/40" data-id="element-2944">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2945">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0" data-id="element-2946">
                        <AlertCircle className="w-5 h-5 text-amber-700" data-id="element-2947" />
                      </div>
                      <div className="min-w-0" data-id="element-2948">
                        <p className="font-bold text-gray-900 text-sm" data-id="element-2949">
                          No truck assigned
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-2950">
                          Assign a truck so this driver can take jobs.
                        </p>
                      </div>
                    </div>
                    {!truckPickerOpen ? <button onClick={() => setTruckPickerOpen(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-bold transition-colors" data-id="element-2951">
                        <TruckIcon className="w-4 h-4" data-id="element-2952" /> Assign a truck
                      </button> : <TruckPickerList trucks={availableTrucks} onPick={handleAssignTruck} onCancel={() => setTruckPickerOpen(false)} data-id="element-2953" />}
                  </div>}
              </div>

              {/* Upcoming */}
              <div className="px-6 pt-5 pb-3" data-id="element-2954">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2955">
                  <CalendarDays className="w-3.5 h-3.5" data-id="element-2956" />
                  Upcoming schedule ({upcoming.length})
                </h3>
                {upcoming.length === 0 ? <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-xl" data-id="element-2957">
                    No upcoming jobs.
                  </p> : <div className="space-y-2" data-id="element-2958">
                    {upcoming.map(j => <JobRow key={j.id} job={j} data-id="element-2959" />)}
                  </div>}
              </div>

              {/* Completed */}
              <div className="px-6 pt-3 pb-6 border-t border-gray-100 mt-2" data-id="element-2960">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2961">
                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2962" />
                  Completed jobs ({completed.length})
                </h3>
                {completed.length === 0 ? <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-xl" data-id="element-2963">
                    No completed jobs yet.
                  </p> : <div className="space-y-2" data-id="element-2964">
                    {completed.map(j => <JobRow key={j.id} job={j} data-id="element-2965" />)}
                  </div>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function TruckPickerList({
  trucks,
  onPick,
  onCancel
}: {
  trucks: ReturnType<typeof useSupplierStore>['trucks'];
  onPick: (truckId: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matching = useMemo(() => {
    if (!q) return trucks;
    return trucks.filter(t => t.plate.toLowerCase().includes(q) || t.type.toLowerCase().includes(q) || t.capacity.toLowerCase().includes(q) || t.status.toLowerCase().includes(q));
  }, [trucks, q]);
  // Suggested: trucks that are ready to take a driver immediately.
  // Priority: Idle first (no current work), then Active (operating but driverless).
  const suggested = useMemo(() => matching.filter(t => t.status === 'Idle' || t.status === 'Active').sort((a, b) => {
    const order = {
      Idle: 0,
      Active: 1
    } as Record<string, number>;
    return (order[a.status] ?? 2) - (order[b.status] ?? 2);
  }).slice(0, 4), [matching]);
  const suggestedIds = new Set(suggested.map(t => t.id));
  const others = matching.filter(t => !suggestedIds.has(t.id));
  return <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-2966">
      <div className="flex items-center justify-between mb-2.5" data-id="element-2967">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-2968">
          Available trucks ({trucks.length})
        </p>
        <button onClick={onCancel} className="text-[11px] font-bold text-gray-500 hover:text-gray-900" data-id="element-2969">
          Cancel
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-3" data-id="element-2970">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2971" />
        <input type="text" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by plate, type, capacity..." className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-2972" />
        {query && <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200" aria-label="Clear search" data-id="element-2973">
            <X className="w-3 h-3 text-gray-500" data-id="element-2974" />
          </button>}
      </div>

      {trucks.length === 0 ? <p className="text-xs text-gray-500 py-3 text-center bg-gray-50 rounded-lg" data-id="element-2975">
          No available trucks — all are currently assigned.
        </p> : matching.length === 0 ? <p className="text-xs text-gray-500 py-3 text-center bg-gray-50 rounded-lg" data-id="element-2976">
          No trucks match "{query}".
        </p> : <div className="max-h-72 overflow-y-auto space-y-3 pr-1" data-id="element-2977">
          {/* Suggested */}
          {!q && suggested.length > 0 && <div data-id="element-2978">
              <p className="text-[10px] font-bold text-brand-600 uppercase tracking-wider mb-1.5 flex items-center gap-1" data-id="element-2979">
                <Sparkles className="w-3 h-3" data-id="element-2980" /> Suggested · ready now
              </p>
              <div className="space-y-1.5" data-id="element-2981">
                {suggested.map(t => <TruckOptionRow key={t.id} truck={t} onPick={onPick} highlight data-id="element-2982" />)}
              </div>
            </div>}
          {/* Others / search results */}
          {(!q && others.length > 0 || q) && <div data-id="element-2983">
              {!q && suggested.length > 0 && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 mt-2" data-id="element-2984">
                  All other trucks ({others.length})
                </p>}
              <div className="space-y-1.5" data-id="element-2985">
                {(q ? matching : others).map(t => <TruckOptionRow key={t.id} truck={t} onPick={onPick} data-id="element-2986" />)}
              </div>
            </div>}
        </div>}
    </div>;
}
function TruckOptionRow({
  truck: t,
  onPick,
  highlight
}: {
  truck: ReturnType<typeof useSupplierStore>['trucks'][number];
  onPick: (truckId: string) => void;
  highlight?: boolean;
}) {
  const statusColor = t.status === 'Idle' ? 'bg-green-100 text-green-700' : t.status === 'Active' ? 'bg-blue-100 text-blue-700' : t.status === 'Maintenance' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
  return <button onClick={() => onPick(t.id)} className={`w-full flex items-center justify-between gap-3 p-2.5 rounded-lg border transition-colors text-left ${highlight ? 'bg-brand-50/40 border-brand-200 hover:border-brand-400 hover:bg-brand-50' : 'bg-white border-gray-200 hover:border-brand-300 hover:bg-brand-50/30'}`} data-id="element-2987">
      <div className="flex items-center gap-2.5 min-w-0" data-id="element-2988">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${highlight ? 'bg-brand-500/10' : 'bg-gray-100'}`} data-id="element-2989">
          <TruckIcon className={`w-4 h-4 ${highlight ? 'text-brand-600' : 'text-gray-600'}`} data-id="element-2990" />
        </div>
        <div className="min-w-0" data-id="element-2991">
          <div className="flex items-center gap-1.5" data-id="element-2992">
            <p className="font-bold text-gray-900 text-sm truncate" data-id="element-2993">
              {t.plate}
            </p>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${statusColor}`} data-id="element-2994">
              {t.status}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 truncate" data-id="element-2995">
            {t.type} · {t.capacity}
          </p>
        </div>
      </div>
      <span className="text-[10px] font-bold text-brand-600 shrink-0" data-id="element-2996">
        Assign →
      </span>
    </button>;
}
function Stat({
  label,
  value,
  accent,
  small
}: {
  label: string;
  value: number | string;
  accent?: 'green';
  small?: boolean;
}) {
  return <div className="bg-white border border-gray-200 rounded-xl p-3" data-id="element-2997">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2998">
        {label}
      </p>
      <p className={`font-display font-bold text-gray-900 ${small ? 'text-base' : 'text-xl'} ${accent === 'green' ? 'text-green-600' : ''}`} data-id="element-2999">
        {value}
      </p>
    </div>;
}
function JobHistoryView({
  jobs,
  drivers,
  search,
  onSelectDriver
}: {
  jobs: Job[];
  drivers: Driver[];
  search: string;
  onSelectDriver: (id: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'active'>('all');
  // Sort newest first using stageHistory.completed or pending_acceptance
  const sorted = useMemo(() => {
    return [...jobs].filter(j => j.assignedDriverId) // only jobs that had a driver
    .sort((a, b) => {
      const aT = a.stageHistory?.completed || a.stageHistory?.accepted || a.stageHistory?.pending_acceptance || 0;
      const bT = b.stageHistory?.completed || b.stageHistory?.accepted || b.stageHistory?.pending_acceptance || 0;
      return bT - aT;
    });
  }, [jobs]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(j => {
      if (statusFilter === 'completed' && j.status !== 'completed') return false;
      if (statusFilter === 'active' && j.status === 'completed') return false;
      if (!q) return true;
      const driver = drivers.find(d => d.id === j.assignedDriverId);
      return j.id.toLowerCase().includes(q) || j.serviceType.toLowerCase().includes(q) || (j.customerName || '').toLowerCase().includes(q) || j.pickup.toLowerCase().includes(q) || j.dropoff.toLowerCase().includes(q) || (driver?.name || '').toLowerCase().includes(q);
    });
  }, [sorted, statusFilter, search, drivers]);
  const formatRelative = (ts?: number) => {
    if (!ts) return '—';
    const diffMin = Math.round((Date.now() - ts) / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const h = Math.round(diffMin / 60);
    if (h < 48) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  };
  return <div className="space-y-4" data-id="element-3000">
      {/* Status filter chips */}
      <div className="flex items-center gap-2" data-id="element-3001">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3002">
          Filter
        </span>
        {[{
        id: 'all',
        label: 'All jobs',
        count: sorted.length
      }, {
        id: 'completed',
        label: 'Completed',
        count: sorted.filter(j => j.status === 'completed').length
      }, {
        id: 'active',
        label: 'Active',
        count: sorted.filter(j => j.status !== 'completed').length
      }].map(f => <button key={f.id} onClick={() => setStatusFilter(f.id as typeof statusFilter)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === f.id ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`} data-id="element-3003">
            {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${statusFilter === f.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-3004">
              {f.count}
            </span>
          </button>)}
        <span className="text-xs text-gray-500 ml-auto" data-id="element-3005">
          Showing {filtered.length} {filtered.length === 1 ? 'job' : 'jobs'}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3006">
        <div className="overflow-x-auto" data-id="element-3007">
          <table className="w-full text-left text-sm" data-id="element-3008">
            <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[10px]" data-id="element-3009">
              <tr data-id="element-3010">
                <th className="px-5 py-3" data-id="element-3011">Job</th>
                <th className="px-5 py-3" data-id="element-3012">Customer</th>
                <th className="px-5 py-3" data-id="element-3013">Driver</th>
                <th className="px-5 py-3" data-id="element-3014">Route</th>
                <th className="px-5 py-3" data-id="element-3015">When</th>
                <th className="px-5 py-3" data-id="element-3016">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-3017">
              {filtered.map(j => {
              const driver = drivers.find(d => d.id === j.assignedDriverId);
              const completedAt = j.stageHistory?.completed;
              return <tr key={j.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-3018">
                    <td className="px-5 py-4 align-top" data-id="element-3019">
                      <p className="font-bold text-gray-900 text-sm" data-id="element-3020">{j.id}</p>
                      <p className="text-xs text-gray-500" data-id="element-3021">{j.serviceType}</p>
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3022">
                      <p className="text-sm text-gray-900 truncate max-w-[180px]" data-id="element-3023">
                        {j.customerName || '—'}
                      </p>
                      {j.customerPhone && <p className="text-[11px] text-gray-500" data-id="element-3024">
                          {j.customerPhone}
                        </p>}
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3025">
                      {driver ? <button onClick={() => onSelectDriver(driver.id)} className="flex items-center gap-2 hover:underline group" data-id="element-3026">
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs shrink-0" data-id="element-3027">
                            {driver.name.charAt(0)}
                          </div>
                          <span className="font-bold text-gray-900 text-sm group-hover:text-brand-600 transition-colors" data-id="element-3028">
                            {driver.name}
                          </span>
                        </button> : <span className="text-xs text-gray-400" data-id="element-3029">
                          Unassigned
                        </span>}
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3030">
                      <p className="text-xs text-gray-700 truncate max-w-[220px]" data-id="element-3031">
                        {j.pickup}
                      </p>
                      {j.dropoff && j.dropoff !== j.pickup && <p className="text-[11px] text-gray-500 truncate max-w-[220px]" data-id="element-3032">
                          → {j.dropoff}
                        </p>}
                    </td>
                    <td className="px-5 py-4 align-top whitespace-nowrap" data-id="element-3033">
                      <p className="text-xs text-gray-700 capitalize" data-id="element-3034">
                        {j.scheduledDate}
                      </p>
                      <p className="text-[11px] text-gray-500" data-id="element-3035">
                        {completedAt ? formatRelative(completedAt) : `${j.startHour}:00`}
                      </p>
                    </td>
                    <td className="px-5 py-4 align-top" data-id="element-3036">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${j.status === 'completed' ? 'bg-green-50 text-green-700 border-green-100' : j.status === 'unassigned' ? 'bg-gray-50 text-gray-600 border-gray-100' : 'bg-purple-50 text-purple-700 border-purple-100'}`} data-id="element-3037">
                        {j.status === 'completed' ? <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-3038" /> : null}
                        {j.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>;
            })}
              {filtered.length === 0 && <tr data-id="element-3039">
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-500 text-sm" data-id="element-3040">
                    No jobs match your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function JobRow({
  job
}: {
  job: Job;
}) {
  return <div className="p-3 rounded-xl border border-gray-200 bg-white" data-id="element-3041">
      <div className="flex items-center justify-between mb-1.5 gap-2" data-id="element-3042">
        <div className="flex items-center gap-2 min-w-0" data-id="element-3043">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0" data-id="element-3044">
            {job.id}
          </span>
          <span className="font-bold text-gray-900 text-sm truncate" data-id="element-3045">
            {job.serviceType}
          </span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border shrink-0 ${getJobStatusColor(job.status)}`} data-id="element-3046">
          {getJobStatusLabel(job.status)}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600 mb-1" data-id="element-3047">
        <span className="flex items-center gap-1" data-id="element-3048">
          <Calendar className="w-3 h-3 text-gray-400" data-id="element-3049" />
          {job.scheduledDate === 'today' ? 'Today' : job.scheduledDate === 'tomorrow' ? 'Tomorrow' : job.scheduledDate === 'yesterday' ? 'Yesterday' : job.scheduledDate}
        </span>
        <span className="flex items-center gap-1" data-id="element-3050">
          <Clock className="w-3 h-3 text-gray-400" data-id="element-3051" />
          {formatTime(job.startHour)} ({job.durationHours}h)
        </span>
      </div>
      <p className="text-xs text-gray-500 truncate" data-id="element-3052">
        <MapPin className="w-3 h-3 inline mr-1 text-gray-400" data-id="element-3053" />
        {job.pickup}
        {job.dropoff && job.dropoff !== job.pickup ? ` → ${job.dropoff}` : ''}
      </p>
    </div>;
}
export function SupplierDrivers() {
  const {
    drivers,
    trucks,
    jobs,
    addDriver
  } = useSupplierStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [view, setView] = useState<'list' | 'schedule' | 'history'>('list');
  const [openDriverId, setOpenDriverId] = useState<string | null>(null);
  const filtered = useMemo(() => {
    return drivers.filter(d => {
      if (search && !d.name.toLowerCase().includes(search.toLowerCase()) && !d.phone.includes(search) && !d.license.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      return true;
    });
  }, [drivers, search, statusFilter]);
  const unassignedCount = drivers.filter(d => !d.assignedTruckId).length;
  const openDriver = drivers.find(d => d.id === openDriverId) || null;
  return <SupplierLayout data-id="element-3054">
      <div className="max-w-6xl mx-auto" data-id="element-3055">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4" data-id="element-3056">
          <div data-id="element-3057">
            <h1 className="text-2xl font-display font-bold text-gray-900" data-id="element-3058">
              Driver Roster
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-3059">
              {drivers.length} registered driver
              {drivers.length === 1 ? '' : 's'}
              {unassignedCount > 0 && <>
                  {' '}
                  ·{' '}
                  <span className="text-amber-700 font-semibold" data-id="element-3060">
                    {unassignedCount} without a truck
                  </span>
                </>}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-3061">
            <button onClick={() => setBulkUploadOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-3062">
              <Upload className="w-4 h-4" data-id="element-3063" /> Bulk Upload
            </button>
            <button onClick={() => setModalOpen(true)} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors shadow-sm" data-id="element-3064">
              <Plus className="w-4 h-4" data-id="element-3065" /> Add Driver
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4" data-id="element-3066">
          <div className="relative flex-1" data-id="element-3067">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3068" />
            <input type="text" placeholder={view === 'history' ? 'Search jobs by id, service, customer, route, or driver...' : 'Search by name, phone, or license...'} value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3069" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-3070">
            <option value="all" data-id="element-3071">All Status</option>
            <option value="On Duty" data-id="element-3072">On Duty</option>
            <option value="Off Duty" data-id="element-3073">Off Duty</option>
            <option value="On Leave" data-id="element-3074">On Leave</option>
          </select>
          {/* View toggle */}
          <div className="flex items-center bg-gray-100 rounded-xl p-0.5 shrink-0" data-id="element-3075">
            <button onClick={() => setView('list')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3076">
              <List className="w-3.5 h-3.5" data-id="element-3077" /> List
            </button>
            <button onClick={() => setView('schedule')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${view === 'schedule' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3078">
              <CalendarDays className="w-3.5 h-3.5" data-id="element-3079" /> Schedule
            </button>
            <button onClick={() => setView('history')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${view === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3080">
              <History className="w-3.5 h-3.5" data-id="element-3081" /> History
            </button>
          </div>
        </div>

        {view === 'history' ? <JobHistoryView jobs={jobs} drivers={drivers} search={search} onSelectDriver={setOpenDriverId} data-id="element-3082" /> : view === 'list' ? <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3083">
            <div className="overflow-x-auto" data-id="element-3084">
              <table className="w-full text-left text-sm" data-id="element-3085">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[10px]" data-id="element-3086">
                  <tr data-id="element-3087">
                    <th className="px-6 py-4" data-id="element-3088">Driver</th>
                    <th className="px-6 py-4" data-id="element-3089">Contact</th>
                    <th className="px-6 py-4" data-id="element-3090">License</th>
                    <th className="px-6 py-4" data-id="element-3091">Assigned Truck</th>
                    <th className="px-6 py-4" data-id="element-3092">Rating</th>
                    <th className="px-6 py-4" data-id="element-3093">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3094">
                  {filtered.map(driver => {
                const truck = trucks.find(t => t.id === driver.assignedTruckId);
                const isExpiring = new Date(driver.licenseExpiry).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;
                return <tr key={driver.id} onClick={() => setOpenDriverId(driver.id)} className="hover:bg-gray-50/50 transition-colors cursor-pointer" data-id="element-3095">
                        <td className="px-6 py-4" data-id="element-3096">
                          <div className="flex items-center gap-3" data-id="element-3097">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold" data-id="element-3098">
                              {driver.name.charAt(0)}
                            </div>
                            <span className="font-bold text-gray-900 whitespace-nowrap" data-id="element-3099">
                              {driver.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 whitespace-nowrap" data-id="element-3100">
                          <div className="flex items-center gap-1.5" data-id="element-3101">
                            <Phone className="w-3.5 h-3.5" data-id="element-3102" /> {driver.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3103">
                          <p className="font-medium text-gray-900" data-id="element-3104">
                            {driver.license}
                          </p>
                          <p className={`text-[10px] mt-0.5 font-bold ${isExpiring ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3105">
                            Exp: {driver.licenseExpiry}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3106">
                          {truck ? <div data-id="element-3107">
                              <p className="font-bold text-gray-900" data-id="element-3108">
                                {truck.plate}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-3109">
                                {truck.type}
                              </p>
                            </div> : <button onClick={e => {
                      e.stopPropagation();
                      setOpenDriverId(driver.id);
                    }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider transition-colors" data-id="element-3110">
                              <TruckIcon className="w-3 h-3" data-id="element-3111" /> Assign truck
                            </button>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3112">
                          <div className="flex items-center gap-1 font-bold text-gray-900" data-id="element-3113">
                            <Star className="w-4 h-4 text-amber-400 fill-current" data-id="element-3114" />{' '}
                            {driver.rating}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3115">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${driver.status === 'On Duty' ? 'bg-green-100 text-green-700' : driver.status === 'Off Duty' ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'}`} data-id="element-3116">
                            {driver.status}
                          </span>
                        </td>
                      </tr>;
              })}
                  {filtered.length === 0 && <tr data-id="element-3117">
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-3118">
                        No drivers match your filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div> :
      // Schedule view
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3119">
            {filtered.map(driver => {
          const truck = trucks.find(t => t.id === driver.assignedTruckId);
          const dJobs = jobs.filter(j => j.assignedDriverId === driver.id);
          const upcoming = dJobs.filter(j => j.status !== 'completed');
          const completedCount = dJobs.filter(j => j.status === 'completed').length;
          const todayCount = upcoming.filter(j => j.scheduledDate === 'today').length;
          const tomorrowCount = upcoming.filter(j => j.scheduledDate === 'tomorrow').length;
          const nextJob = upcoming.sort((a, b) => {
            const order = {
              today: 0,
              tomorrow: 1
            } as Record<string, number>;
            const oA = order[a.scheduledDate] ?? 2;
            const oB = order[b.scheduledDate] ?? 2;
            if (oA !== oB) return oA - oB;
            return a.startHour - b.startHour;
          })[0];
          return <button key={driver.id} onClick={() => setOpenDriverId(driver.id)} className="text-left bg-white rounded-2xl border border-gray-200 hover:border-brand-300 hover:shadow-md transition-all p-5" data-id="element-3120">
                  <div className="flex items-center justify-between mb-4 gap-3" data-id="element-3121">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-3122">
                      <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold shrink-0" data-id="element-3123">
                        {driver.name.charAt(0)}
                      </div>
                      <div className="min-w-0" data-id="element-3124">
                        <h3 className="font-bold text-gray-900 truncate" data-id="element-3125">
                          {driver.name}
                        </h3>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-3126">
                          {truck ? <>
                              <TruckIcon className="w-3 h-3" data-id="element-3127" /> {truck.plate}
                            </> : <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-id="element-3128">
                              <AlertCircle className="w-3 h-3" data-id="element-3129" /> No truck
                            </span>}
                        </p>
                      </div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${driver.status === 'On Duty' ? 'bg-green-100 text-green-700' : driver.status === 'Off Duty' ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-700'}`} data-id="element-3130">
                      {driver.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-3131">
                    <div className="bg-gray-50 rounded-lg p-2 text-center" data-id="element-3132">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3133">
                        Today
                      </p>
                      <p className="font-display font-bold text-gray-900 text-lg" data-id="element-3134">
                        {todayCount}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center" data-id="element-3135">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3136">
                        Tomorrow
                      </p>
                      <p className="font-display font-bold text-gray-900 text-lg" data-id="element-3137">
                        {tomorrowCount}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center" data-id="element-3138">
                      <p className="text-[9px] font-bold text-green-700 uppercase tracking-wider" data-id="element-3139">
                        Completed
                      </p>
                      <p className="font-display font-bold text-green-700 text-lg" data-id="element-3140">
                        {completedCount}
                      </p>
                    </div>
                  </div>

                  {nextJob ? <div className="p-3 rounded-xl bg-gray-50 border border-gray-100" data-id="element-3141">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-3142">
                        Next up
                      </p>
                      <p className="font-bold text-gray-900 text-sm truncate" data-id="element-3143">
                        {nextJob.serviceType}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-2 mt-1" data-id="element-3144">
                        <span className="flex items-center gap-1" data-id="element-3145">
                          <Calendar className="w-3 h-3" data-id="element-3146" />
                          {nextJob.scheduledDate === 'today' ? 'Today' : 'Tomorrow'}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-3147">
                          <Clock className="w-3 h-3" data-id="element-3148" />
                          {formatTime(nextJob.startHour)}
                        </span>
                      </p>
                    </div> : <div className="p-3 rounded-xl bg-gray-50 border border-dashed border-gray-200 text-center" data-id="element-3149">
                      <p className="text-xs text-gray-500" data-id="element-3150">No upcoming jobs</p>
                    </div>}
                </button>;
        })}
            {filtered.length === 0 && <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500 text-sm" data-id="element-3151">
                No drivers match your filters.
              </div>}
          </div>}
      </div>

      <AddDriverModal open={modalOpen} onClose={() => setModalOpen(false)} data-id="element-3152" />
      <AnimatePresence data-id="element-3153">
        {bulkUploadOpen && <BulkUploadDriversModal onClose={() => setBulkUploadOpen(false)} onImport={rows => {
        let count = 0;
        rows.forEach(r => {
          addDriver({
            name: r.name,
            phone: r.phone,
            license: r.license,
            licenseExpiry: r.licenseExpiry,
            status: r.status,
            rating: r.rating
          });
          count++;
        });
        toast.success(`${count} driver${count !== 1 ? 's' : ''} imported successfully`);
        setBulkUploadOpen(false);
      }} data-id="element-3154" />}
      </AnimatePresence>
      <DriverDetailDrawer driver={openDriver} jobs={jobs} trucks={trucks} drivers={drivers} onClose={() => setOpenDriverId(null)} data-id="element-3155" />
    </SupplierLayout>;
}
// ============ Bulk Upload Drivers Modal ============
type ParsedDriverRow = {
  rowNumber: number;
  name: string;
  phone: string;
  license: string;
  licenseExpiry: string;
  status: Driver['status'];
  rating: number;
  errors: string[];
};
function parseDriverCSV(text: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  for (const line of lines) {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    fields.push(current.trim());
    rows.push(fields);
  }
  return rows;
}
function BulkUploadDriversModal({
  onClose,
  onImport
}: {
  onClose: () => void;
  onImport: (rows: ParsedDriverRow[]) => void;
}) {
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedDriverRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDownloadTemplate = () => {
    const headers = ['name', 'phone', 'license', 'licenseExpiry', 'status', 'rating'];
    const sampleRows = [['Ahmed Khan', '+971 50 123 4567', 'DL-1234567', '2026-08-15', 'Available', '4.8'], ['Sara Al Mansoori', '+971 55 987 6543', 'DL-7654321', '2027-02-20', 'Off Duty', '4.9'], ['Mohammed Raza', '+971 52 111 2233', 'DL-8901234', '2025-11-10', 'Available', '4.6']];
    const csv = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'drivers-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };
  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const rows = parseDriverCSV(text);
      if (rows.length < 2) {
        toast.error('CSV file is empty or missing data rows');
        return;
      }
      const headers = rows[0].map(h => h.toLowerCase());
      const nameIdx = headers.indexOf('name');
      const phoneIdx = headers.indexOf('phone');
      const licenseIdx = headers.indexOf('license');
      const expiryIdx = headers.indexOf('licenseexpiry');
      const statusIdx = headers.indexOf('status');
      const ratingIdx = headers.indexOf('rating');
      if (nameIdx === -1 || phoneIdx === -1 || licenseIdx === -1 || expiryIdx === -1) {
        toast.error('CSV must include columns: name, phone, license, licenseExpiry');
        return;
      }
      const validStatuses: Driver['status'][] = ['Available', 'On Duty', 'Off Duty'];
      const parsed: ParsedDriverRow[] = rows.slice(1).map((row, idx) => {
        const errors: string[] = [];
        const name = row[nameIdx] || '';
        const phone = row[phoneIdx] || '';
        const license = row[licenseIdx] || '';
        const licenseExpiry = row[expiryIdx] || '';
        const statusRaw = statusIdx !== -1 ? row[statusIdx] || 'Off Duty' : 'Off Duty';
        const ratingRaw = ratingIdx !== -1 ? row[ratingIdx] : '5.0';
        if (!name) errors.push('Name required');
        if (!phone) errors.push('Phone required');
        if (!license) errors.push('License required');
        if (!licenseExpiry) errors.push('License expiry required');
        // Validate date format (YYYY-MM-DD)
        if (licenseExpiry && !/^\d{4}-\d{2}-\d{2}$/.test(licenseExpiry)) {
          errors.push('Expiry must be YYYY-MM-DD');
        }
        const matchedStatus = validStatuses.find(s => s.toLowerCase() === statusRaw.toLowerCase());
        if (statusRaw && !matchedStatus) {
          errors.push(`Invalid status "${statusRaw}"`);
        }
        const rating = parseFloat(ratingRaw);
        if (ratingRaw && (isNaN(rating) || rating < 0 || rating > 5)) {
          errors.push('Rating must be 0-5');
        }
        return {
          rowNumber: idx + 2,
          name,
          phone,
          license,
          licenseExpiry,
          status: matchedStatus || 'Off Duty',
          rating: isNaN(rating) ? 5.0 : rating,
          errors
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };
  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" data-id="element-3156">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3157">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-3158">
          <div data-id="element-3159">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-3160">
              <Upload className="w-5 h-5 text-blue-600" data-id="element-3161" />
              Bulk Upload Drivers
            </h3>
            <p className="text-sm text-gray-500" data-id="element-3162">
              {step === 'upload' ? 'Upload a CSV file to import multiple drivers at once' : `Review ${parsedRows.length} row${parsedRows.length !== 1 ? 's' : ''} before importing`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-3163">
            <X className="w-5 h-5" data-id="element-3164" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-3165">
          {step === 'upload' ? <div className="space-y-6" data-id="element-3166">
              <div className="flex items-start gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl" data-id="element-3167">
                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0" data-id="element-3168">
                  <FileSpreadsheet className="w-5 h-5" data-id="element-3169" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-3170">
                  <h4 className="text-sm font-bold text-gray-900" data-id="element-3171">
                    New here? Start with our template
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5" data-id="element-3172">
                    Download the CSV template with sample rows and the exact
                    column format we expect.
                  </p>
                </div>
                <button onClick={handleDownloadTemplate} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-50 transition-colors" data-id="element-3173">
                  <Download className="w-3.5 h-3.5" data-id="element-3174" />
                  Download
                </button>
              </div>

              <div onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3175">
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }} data-id="element-3176" />
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-3177" />
                <p className="text-sm font-bold text-gray-900" data-id="element-3178">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-3179">CSV file, max 5MB</p>
              </div>

              <div className="text-xs text-gray-500" data-id="element-3180">
                <p className="font-semibold text-gray-700 mb-1" data-id="element-3181">
                  Required columns:
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-3182">
                  {['name', 'phone', 'license', 'licenseExpiry'].map(c => <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono" data-id="element-3183">
                      {c}
                    </span>)}
                </div>
                <p className="font-semibold text-gray-700 mt-2 mb-1" data-id="element-3184">
                  Optional columns:
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-3185">
                  {['status', 'rating'].map(c => <span key={c} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono" data-id="element-3186">
                      {c}
                    </span>)}
                </div>
              </div>
            </div> : <div className="space-y-4" data-id="element-3187">
              <div className="grid grid-cols-2 gap-3" data-id="element-3188">
                <div className="p-3 bg-green-50 border border-green-100 rounded-xl" data-id="element-3189">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3190">
                    <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-3191" />
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-3192">
                      Ready to import
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-3193">
                    {validRows.length}
                  </p>
                </div>
                <div className={`p-3 border rounded-xl ${invalidRows.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`} data-id="element-3194">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3195">
                    <AlertCircle className={`w-4 h-4 ${invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3196" />
                    <p className={`text-xs font-bold uppercase tracking-wider ${invalidRows.length > 0 ? 'text-red-700' : 'text-gray-500'}`} data-id="element-3197">
                      Has errors
                    </p>
                  </div>
                  <p className="text-2xl font-display font-bold text-gray-900" data-id="element-3198">
                    {invalidRows.length}
                  </p>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-3199">
                File:{' '}
                <span className="font-medium text-gray-700" data-id="element-3200">{fileName}</span>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-3201">
                <div className="max-h-80 overflow-y-auto" data-id="element-3202">
                  <table className="w-full text-xs" data-id="element-3203">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider sticky top-0" data-id="element-3204">
                      <tr data-id="element-3205">
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3206">#</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3207">Name</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3208">Phone</th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3209">
                          License
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3210">
                          Expiry
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3211">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left font-bold" data-id="element-3212">
                          Issues
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-3213">
                      {parsedRows.map(row => <tr key={row.rowNumber} className={row.errors.length > 0 ? 'bg-red-50/50' : ''} data-id="element-3214">
                          <td className="px-3 py-2 text-gray-500 font-mono" data-id="element-3215">
                            {row.rowNumber}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900" data-id="element-3216">
                            {row.name || <span className="text-red-500 italic" data-id="element-3217">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3218">
                            {row.phone || <span className="text-red-500 italic" data-id="element-3219">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3220">
                            {row.license || <span className="text-red-500 italic" data-id="element-3221">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3222">
                            {row.licenseExpiry || <span className="text-red-500 italic" data-id="element-3223">empty</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-700" data-id="element-3224">
                            {row.status}
                          </td>
                          <td className="px-3 py-2" data-id="element-3225">
                            {row.errors.length === 0 ? <span className="inline-flex items-center gap-1 text-green-600" data-id="element-3226">
                                <CheckCircle2 className="w-3 h-3" data-id="element-3227" /> OK
                              </span> : <span className="text-red-600" data-id="element-3228">
                                {row.errors.join(', ')}
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0" data-id="element-3229">
          {step === 'preview' ? <button onClick={() => {
          setStep('upload');
          setParsedRows([]);
          setFileName('');
        }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-xl transition-colors" data-id="element-3230">
              Back
            </button> : <div data-id="element-3231"></div>}
          <div className="flex gap-3" data-id="element-3232">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl" data-id="element-3233">
              Cancel
            </button>
            {step === 'preview' && <button onClick={() => onImport(validRows)} disabled={validRows.length === 0} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl" data-id="element-3234">
                Import {validRows.length} driver
                {validRows.length !== 1 ? 's' : ''}
              </button>}
          </div>
        </div>
      </motion.div>
    </div>;
}